import React, { useEffect, useState } from "react";
import UIkit from "uikit";
import { Link, useHistory } from "react-router-dom";
import { usePermissionsStore } from "./permissions_store";
import { roleName, roleDescription } from "./role-presentation";

const isTemporaryFallbackRole = (role) =>
  String(role?.name || "").trim().toLowerCase() === "no assigned role";

export default function RolesPage() {
  const { roles, rolesLoaded, rolesError, loadRoles, createRole, duplicateRole, deleteRole, canEdit, isOwner } = usePermissionsStore();
  const history = useHistory();
  const [duplicateSource, setDuplicateSource] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [saving, setSaving] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    if (!rolesLoaded) loadRoles();
  }, [rolesLoaded, loadRoles]);

  useEffect(() => {
    const modal = UIkit.modal("#modal-role-delete");

    if (roleToDelete) {
      modal.show();
      return;
    }

    modal.hide();
  }, [roleToDelete]);

  const handleAddRole = () => {
    setDuplicateSource(null);
    setNewName("");
    setNewDesc("");
    setCreating(true);
  };

  const handleDuplicate = (role) => {
    setDuplicateSource(role);
    setNewName(`${roleName(role)} (Company)`);
    setNewDesc(roleDescription(role) || "");
    setCreating(true);
  };

  const handleCancelCreate = () => {
    setNewName("");
    setNewDesc("");
    setCreating(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!canEdit("admin.roles") || (duplicateSource?.grants_admin && !isOwner)) return;
    if (!newName.trim()) {
      UIkit.notification("Role name is required", { status: "warning" });
      return;
    }
    setSaving(true);
    try {
      const payload = { name: newName.trim(), description: newDesc.trim() };
      const id = duplicateSource ? await duplicateRole(duplicateSource.id, payload) : await createRole(payload);
      setNewName("");
      setNewDesc("");
      setCreating(false);
      UIkit.offcanvas("#modal-role-add").hide();
      UIkit.notification("Role created", { status: "success" });
      history.push(`/roles/${id}`);
    } catch (err) {
      UIkit.notification(err?.response?.data?.error || "Failed to create role", { status: "danger" });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (role) => {
    setRoleToDelete(role);
  };

  const handleCancelDelete = () => {
    if (deleting) {
      return;
    }
    setRoleToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!canEdit("admin.roles") || !roleToDelete || (roleToDelete.grants_admin && !isOwner)) {
      return;
    }

    setDeleting(true);

    try {
      await deleteRole(roleToDelete.id);
      UIkit.notification("Role deleted", { status: "success" });
      setRoleToDelete(null);
    } catch {
      UIkit.notification("Failed to delete role", { status: "danger" });
    } finally {
      setDeleting(false);
    }
  };

  const canManage = canEdit("admin.roles");
  const visibleRoles = roles.filter((role) => !isTemporaryFallbackRole(role));

  return (
    <section id="roles-page">
      <div id="hms-page-title">
        <div className="uk-flex uk-flex-between uk-flex-middle">
          <h1 className="uk-heading-small">Roles &amp; Permissions</h1>
        </div>
      </div>

      <div id="hms-main-body">
        {canManage && (
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div className="hms-guest-btn">
              <button
                className="uk-button hms-btn hms-red-btn"
                uk-toggle="target: #modal-role-add"
                onClick={handleAddRole}
                disabled={!rolesLoaded || Boolean(rolesError)}
              >
                Add Role
              </button>
            </div>
          </div>
        )}
        <div className="secondary uk-margin-small-bottom">
          <strong>System roles</strong> are shared presets used by every company. <strong>Duplicate</strong> a preset to edit permissions for your company; the copy appears as a <strong>Company</strong> role and affects only users assigned to it. <br/>Each user has <strong>one role</strong>. The account owner always has full access. Only the owner can appoint administrators or remove users.
        </div>

        {rolesError && (
          <div role="alert" className="uk-alert-danger" uk-alert="true">
            <p>{rolesError}</p>
            <button className="uk-button hms-btn" onClick={loadRoles}>Retry</button>
          </div>
        )}

        <div className="uk-card uk-card-default">
          <div className="hms-simplebar" data-simplebar>
            <table className="uk-table uk-table-divider hms-table">
              <thead>
                <tr>
                  <th>Role</th>
                  <th>Description</th>
                  <th className="uk-text-center">Users</th>
                  <th className="uk-text-center">Type</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {!rolesLoaded && (
                  <tr>
                    <td colSpan={5} className="uk-text-center secondary">Loading…</td>
                  </tr>
                )}
                {rolesLoaded && !rolesError && visibleRoles.length === 0 && (
                  <tr>
                    <td colSpan={5} className="uk-text-center secondary">No roles found</td>
                  </tr>
                )}
                {visibleRoles.map((role) => (
                  <tr key={role.id}>
                    <td><strong>{roleName(role)}</strong></td>
                    <td className="secondary">{roleDescription(role)}</td>
                    <td className="uk-text-center">{role.user_count ?? 0}</td>
                    <td className="uk-text-center">
                      {role.is_system_role ? (
                        <span className="hms-booking-tag">System</span>
                      ) : (
                        <span className="hms-chip priority-medium">Company</span>
                      )}
                    </td>
                      <td className="hms-action-btn">
                        <Link to={`/roles/${role.id}`} className="uk-button"
                          aria-label={!role.is_system_role && canManage && (isOwner || !role.grants_admin) ? "Edit permissions" : "View permissions"}
                          title={!role.is_system_role && canManage && (isOwner || !role.grants_admin) ? "Edit permissions" : "View permissions"}>
                          <img src={!role.is_system_role && canManage && (isOwner || !role.grants_admin) ? "/images/edit.svg" : "/images/view.svg"} alt="" />
                        </Link>
                        {canManage && (isOwner || !role.grants_admin) && (
                          <>
                            <button type="button" className="uk-button" aria-label="Duplicate" title="Duplicate to customize permissions" uk-toggle="target: #modal-role-add" onClick={() => handleDuplicate(role)}>
                              <img src="/images/clone.svg" alt="" />
                            </button>
                            {!role.is_system_role && <button type="button" className="uk-button" aria-label="Delete" title="Delete role" onClick={() => handleDelete(role)}>
                              <img src="/images/delete.svg" alt="" />
                            </button>}
                          </>
                        )}
                      </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div
        id="modal-role-add"
        className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
        uk-offcanvas="flip: true; overlay: true"
      >
        <div className="uk-offcanvas-bar">
          <div className="hms-modals-main">
            <button
              className="uk-offcanvas-close uk-close-large"
              type="button"
              uk-close="true"
              onClick={handleCancelCreate}
            ></button>
            <div className="uk-modal-header">
              <h2 className="uk-modal-title">{duplicateSource ? `Duplicate ${roleName(duplicateSource)}` : "Add Role"}</h2>
            </div>
            <div className="uk-modal-body">
              {duplicateSource && <p>The copy starts with the same permissions. You can edit it next. Existing users keep their current role until you assign the new one.</p>}
              <form onSubmit={handleCreate}>
                <div className="uk-grid-small" uk-grid="true">
                  <div className="uk-width-1-1">
                    <label className="uk-form-label" htmlFor="new-role-name">Role Name *</label>
                    <input
                      id="new-role-name"
                      className="uk-input"
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      placeholder="e.g. Regional Manager"
                      autoFocus={creating}
                    />
                  </div>
                  <div className="uk-width-1-1">
                    <label className="uk-form-label" htmlFor="new-role-description">Description</label>
                    <input
                      id="new-role-description"
                      className="uk-input"
                      value={newDesc}
                      onChange={(e) => setNewDesc(e.target.value)}
                      placeholder="Optional description"
                    />
                  </div>
                </div>
                <div className="uk-margin-top">
                  <button
                    type="submit"
                    className="uk-button hms-btn hms-red-btn"
                    disabled={saving}
                  >
                    {saving ? "Saving…" : duplicateSource ? "Create company copy" : "Create role"}
                  </button>
                  <button
                    type="button"
                    className="uk-button hms-btn uk-offcanvas-close"
                    onClick={handleCancelCreate}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>

      <div
        id="modal-role-delete"
        className="uk-modal"
        uk-modal="true"
      >
        <div className="uk-modal-dialog uk-modal-body">
          <button
            className="uk-modal-close-default"
            type="button"
            uk-close="true"
            onClick={handleCancelDelete}
            disabled={deleting}
          ></button>
          <h2 className="uk-modal-title">Delete Role</h2>
          <p>
            Delete role "{roleToDelete?.name}"? This cannot be undone.
          </p>
          <div className="uk-flex uk-flex-right uk-margin-top">
            <button
              type="button"
              className="uk-button uk-button-default uk-modal-close"
              onClick={handleCancelDelete}
              disabled={deleting}
              autoFocus={!!roleToDelete}
            >
              Cancel
            </button>
            <button
              type="button"
              className="uk-button uk-button-danger uk-margin-small-left"
              onClick={handleConfirmDelete}
              disabled={deleting}
            >
              {deleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
